import "../groups/GroupCardShared.scss";

import clsx from "clsx";
import { format } from "date-fns";
import { FC, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { DueDateButton } from "../DueDateButton";
import { PageLockedChip } from "../PageLockedChip";
import { isSubmissionLate } from "../helpers";
import { PointsConfig } from "../outcomes/PointsConfig";

import { INDEX_NOT_FOUND, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessmentBanner } from "components/materials/page/banners/AssessmentBanner";
import { GroupConfigCard } from "components/materials/page/groups/GroupConfigCard";
import { StudentGroupCard } from "components/materials/page/groups/StudentGroupCard";
import { StudentSelfSelectCard } from "components/materials/page/groups/StudentSelfSelectCard";
import {
  findPageGroupIndexOfAccessId,
  findPageGroupIndexOfStudentUser,
} from "components/materials/page/groups/helpers";
import { InteractionModeMenu } from "components/materials/page/interaction-mode/InteractionModeMenu";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import {
  AssessmentType,
  InteractionMode,
  PageGroupUserType,
  PageType,
  SubmissionType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { showConfirmationModal } from "mds/components/Modal";
import { ORDER_BY_CREATED, useCustomOrdered } from "mds/hooks/use-ordered";
import { useCapabilities, useSectionUserIdsWithSectionCapability } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  NestedPageOutcomeType,
  SectionCapability,
  selectAssessmentsByPageId,
  selectCurrentSectionId,
  selectCurrentSectionIsTemplate,
  selectCurrentSectionUserId,
  selectCurrentUserIsEnabledForSection,
  selectIsEditModeEnabled,
  selectIsGroupConfigShown,
  selectNestedPageOutcomes,
  selectPageGroupsWithUsersByPageId,
  selectQueryString,
  selectSelectedPageTabVariant,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastSuccessMessage } from "utils/alerts";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { usePageActions } from "utils/page";
import {
  GROUP_INTERACTION_MODES,
  isGroupInteractionMode,
  isSharedClassInteractionMode,
  isSharedClassPage,
} from "utils/page-utils";
import { useQueryParamsManager } from "utils/routing";

type PageOverviewHeaderProps = {
  page: PageType;
  className?: string;
  isStaticContent?: boolean;
  readOnly?: boolean;
  selectedAccessId?: string | null;
  isProjectorView?: boolean;
};

const getPointsConfigTooltip = (
  sharedClassPage: boolean,
  canAuthorSection: boolean,
  assessments: AssessmentType[],
  pageOutcomes: NestedPageOutcomeType[],
) => {
  if (sharedClassPage) {
    return t("tooltip.points_config_shared_class");
  }

  if (canAuthorSection && assessments.length > 0) {
    return t("tooltip.points_config_has_assessments");
  }

  if (canAuthorSection && pageOutcomes.length === 0) {
    return t("page_overview_header.disabled_info");
  }

  return t("tooltip.assessment_points");
};

export const PageOverviewHeader: FC<PageOverviewHeaderProps> = ({
  page,
  className,
  isStaticContent,
  readOnly = false,
  selectedAccessId,
  isProjectorView = false,
}) => {
  const dispatch = useAppDispatch();
  const queryString = useAppSelector(selectQueryString);
  const { savePage } = usePageActions();
  const { canAuthorSection, canParticipateInSection } = useCapabilities();
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isAssessing = useIsAssessing();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isGroupConfigShown = useAppSelector(selectIsGroupConfigShown);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const sharedClassPage = isSharedClassPage(page);
  const canShowToast = useRef(true);
  const pageId = page.id;
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const currentUserEnabled = useAppSelector(selectCurrentUserIsEnabledForSection);
  const canHaveSubmission = canParticipateInSection && currentUserEnabled;
  const { setQueryParams } = useQueryParamsManager();
  const pageGroups = useCustomOrdered(
    useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, pageId)),
    ORDER_BY_CREATED,
  );
  const canAuthorSectionSectionUserIds = useSectionUserIdsWithSectionCapability(
    SectionCapability.CAN_AUTHOR_SECTION,
  );
  const canAuthorSectionSectionUserIdsKey = canAuthorSectionSectionUserIds.join(",");
  const showLockedChip = page.locked_at;

  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, pageId));
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, pageId));
  const userPageGroupId = pageGroups.find((pg) =>
    pg.page_group_users.some((pgu) => pgu.section_user_id === sectionUserId),
  )?.id;

  const [showStudentGroupCard, setShowStudentGroupCard] = useState(true);

  const pageGroupIndex = !canAuthorSection
    ? findPageGroupIndexOfStudentUser(pageGroups, sectionUserId)
    : findPageGroupIndexOfAccessId(pageGroups, selectedAccessId);

  const isShowingStudentSelfSelectCard = Boolean(
    canParticipateInSection &&
      page.interaction_mode === InteractionMode.SELF_ASSIGN &&
      pageGroupIndex === INDEX_NOT_FOUND,
  );

  const canSeeStudentGroupCard = Boolean(
    ((canParticipateInSection && GROUP_INTERACTION_MODES.includes(page.interaction_mode)) ||
      (canAuthorSection &&
        GROUP_INTERACTION_MODES.includes(page.interaction_mode) &&
        (!isAssessing || (isAssessing && !!page.assessments_published_at)))) &&
      pageGroupIndex !== INDEX_NOT_FOUND,
  );

  const isShowingStudentGroupCard =
    showStudentGroupCard && canSeeStudentGroupCard && !isGroupConfigShown;

  const isShowingGroupConfigurationCard = isGroupConfigShown && !isShowingStudentGroupCard;

  const isShowingCurrentStudentNotInGroupBanner = Boolean(
    canParticipateInSection &&
      page.interaction_mode === InteractionMode.ASSIGN_STUDENTS &&
      pageGroupIndex === INDEX_NOT_FOUND,
  );

  const canConfigurePoints = Boolean(
    pageOutcomes.length > 0 && canAuthorSection && !sharedClassPage && assessments.length === 0,
  );
  const isPointsReadOnly = readOnly || !canAuthorSection || !isEditModeEnabled;

  useEffect(() => {
    dispatch(viewStateActions.setShowGroupConfig(false));
    setShowStudentGroupCard(true);
  }, [queryString, selectedAccessId, page.id, dispatch]);

  useEffect(() => {
    return addBroadcastListener(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, (message) => {
      // Notify students when they've submitted a late submission or when the instructor has collected their submission,
      // or when they have resubmitted before the due date.
      // Determine if we should show a modal that a submission was created.
      if (
        message.model === "submissions" &&
        message.action === "create" &&
        canShowToast.current &&
        canHaveSubmission
      ) {
        const submissionMessage = message.data as SubmissionType;
        if (submissionMessage.page_id !== pageId) {
          // Submission is for a different page, ignore it.
          return;
        }

        // If it's for a student we only want to show the modal if it's their submission.
        if (submissionMessage.section_user_id) {
          if (submissionMessage.section_user_id !== sectionUserId) {
            return;
          }
        } else if (submissionMessage.page_group_id !== userPageGroupId) {
          return;
        }

        // Don't allow toasting again in 5 seconds.
        canShowToast.current = false;

        if (tabVariant !== "my_draft") {
          toastSuccessMessage(t("success.toasts.submission_success_one"));
        } else {
          const submittedBySectionUserId = submissionMessage.submitted_by_id;
          const isSubmittedBySystem = !submittedBySectionUserId;
          const isSubmittedByInstructor =
            !!submittedBySectionUserId &&
            canAuthorSectionSectionUserIds.includes(submittedBySectionUserId);

          // Determine if submission was created after due date (more than 1 minute after)
          const isLateSubmission = isSubmissionLate(submissionMessage, page?.due_at);

          const submissionCreatedAt = new Date(submissionMessage.created_at);
          const dueDate = page.due_at ? new Date(page.due_at) : null;
          const isBeforeDueDate = dueDate && submissionCreatedAt < dueDate;
          const dateFormat = "MMM d, HH:mm";
          const formattedDueDate = dueDate ? format(dueDate, dateFormat) : "";
          const formattedCreatedAt = format(submissionCreatedAt, dateFormat);

          let modalText: string;

          if (isSubmittedBySystem) {
            // Submission was collected by Forum at the due date
            modalText = t("page_overview_header.submission_created_at_due_date", {
              due_date: formattedDueDate,
            });
          } else if (isSubmittedByInstructor) {
            if (isBeforeDueDate) {
              // Instructor collected submissions early
              modalText = t("page_overview_header.submission_created_before_due_date", {
                created_at: formattedCreatedAt,
              });
            } else {
              // Display message of submission collected without noting a future due date
              modalText = t("page_overview_header.submission_created_no_due_date", {
                created_at: formattedCreatedAt,
              });
            }
          } else if (isLateSubmission) {
            // Student submitted late
            modalText = t("page_overview_header.submission_created_user_late", {
              created_at: formattedCreatedAt,
            });
          } else {
            // Student resubmitted before the due date
            modalText = t("page_overview_header.submission_created_user", {
              created_at: formattedCreatedAt,
            });
          }

          // TODO: consider extracting this to a separate component
          showConfirmationModal({
            title: t("page_overview_header.submission_created_title"),
            labelText: modalText,
            confirmButtonText: t("page_overview_header.view_submission"),
            allowManualClose: false,
            hideCancelButton: true,
            hideCloseButton: true,
            onConfirm: () => {
              // Set the submission ID query param to navigate to the submission
              setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: submissionMessage.id });
              dispatch(viewStateActions.setShouldResetPageTab(true));
            },
          });
        }

        // Allow toasting again in 5 seconds.
        setTimeout(() => {
          canShowToast.current = true;
        }, 5000);
      }

      // Refresh page access data when the current student is added to a page group.
      // This covers the case where an instructor assigns a student to a group (vs. self-assign),
      // and the student's page access data should be re-fetched under the new group principal.
      else if (
        message.model === "page_group_users" &&
        (message.action === "create" || message.action === "update") &&
        sectionUserId
      ) {
        const pageGroupUser = message.data as PageGroupUserType;
        const isForCurrentUser = pageGroupUser.section_user_id === sectionUserId;
        const isForThisPage = pageGroups.some((pg) => pg.id === pageGroupUser.page_group_id);
        if (isForCurrentUser && isForThisPage) {
          storeApi.page_access_data.list({ page_id: pageId }, { skipCache: true, skipToast: true });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageId,
    sectionUserId,
    canHaveSubmission,
    userPageGroupId,
    page.due_at,
    page.submissions_last_collected_at,
    tabVariant,
    pageGroups,
    // eslint-disable-next-line no-object-deps/no-object-deps
    dispatch,
    // eslint-disable-next-line no-object-deps/no-object-deps
    setQueryParams,
    canAuthorSectionSectionUserIdsKey,
  ]);

  useEffect(() => {
    if (isGroupInteractionMode(page.interaction_mode)) {
      storeApi.page_group_users.list({ page_group__page_id: page.id });
      storeApi.page_groups.list({ page_id: page.id });
    }
  }, [page.interaction_mode, page.id, sectionId]);

  useEffect(() => {
    dispatch(viewStateActions.setShowGroupConfig(false));
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [isAssessing, dispatch]);

  // Refresh page group data when window regains focus
  // This is a temporary fix for issues where group distribution looks different for different users, possibly due to
  // issues with pubsub. If it's not due to pubsub, this fix will likely do nothing. Wait and re-evaluate.
  // TODO: Store: If we have versioning in websocket messages and a replay feature,
  // we should replay missed messages instead.
  useEffect(() => {
    if (
      !page?.id ||
      [InteractionMode.INDIVIDUAL, InteractionMode.SHARED_CLASS].includes(page.interaction_mode)
    ) {
      return;
    }
    const fetchPageGroupData = () => {
      return Promise.all([
        storeApi.page_group_users.list(
          { page_group__page_id: page.id },
          { skipCache: true, skipToast: true },
        ),
        storeApi.page_groups.list({ page_id: page.id }, { skipCache: true, skipToast: true }),
      ]);
    };

    window.addEventListener("focus", fetchPageGroupData);
    return () => {
      window.removeEventListener("focus", fetchPageGroupData);
    };
  }, [page.interaction_mode, page.id, sectionId]);

  if (!page) {
    return null;
  }

  const onChangeInteractionMode = (interactionMode: InteractionMode) => {
    const isNowGrouped = isGroupInteractionMode(interactionMode);
    const isNowShared = isSharedClassInteractionMode(interactionMode);

    const pageValues: Partial<PageType> & { id: string } = {
      id: page.id,
      interaction_mode: interactionMode,
    };

    if (interactionMode === page.interaction_mode) {
      if (isNowGrouped) {
        dispatch(viewStateActions.setShowGroupConfig(!isGroupConfigShown));
      }
      return;
    }

    if (!isInTemplateSection) {
      if (isNowGrouped) {
        // If there are no page groups, create two.
        if (pageGroups.length === 0) {
          storeApi.page_groups.create({
            page_id: page.id,
            section_id: page.section_id,
          });
          storeApi.page_groups.create({
            page_id: page.id,
            section_id: page.section_id,
          });
        }

        dispatch(viewStateActions.setShowGroupConfig(!isGroupConfigShown));
      } else {
        // TODO: It's unhandled when one of the page groups fail to be removed. This should also be a batch delete
        //  all or delete many. And: should we really be deleting all page groups when the user switches to a
        //  non-group interaction mode?
        pageGroups.forEach((pageGroup) => {
          storeApi.page_groups.destroy(pageGroup.id);
        });

        dispatch(viewStateActions.setShowGroupConfig(false));
      }
    }

    if (isNowShared) {
      pageValues.max_points = 0;
    }

    storeApi.pages.partial_update(pageValues);
  };

  const showInteractionModeMenu = !isStaticContent;
  const showPointsMenu =
    (canAuthorSection && isEditModeEnabled && !isStaticContent && !readOnly) ||
    page?.max_points > 0;

  return (
    <div className={twMerge("flex w-full flex-col items-center px-4 md:px-10", className)}>
      <div
        className={clsx(
          "flex w-full flex-col",
          !readOnly && "max-w-[--page-default-max-width] gap-5",
        )}
      >
        <div
          className={clsx(
            "page-metadata-container flex w-full flex-wrap items-center gap-2 @container/page-metadata",
            isProjectorView ? "justify-end" : "",
          )}
        >
          {showInteractionModeMenu ? (
            <InteractionModeMenu
              page={page}
              pageGroupIndex={pageGroupIndex}
              readOnly={readOnly || !isEditModeEnabled}
              onAuthorClick={readOnly ? undefined : onChangeInteractionMode}
              onStudentGroupCardToggle={
                GROUP_INTERACTION_MODES.includes(page.interaction_mode) &&
                !isShowingCurrentStudentNotInGroupBanner &&
                canSeeStudentGroupCard
                  ? () => setShowStudentGroupCard(!showStudentGroupCard)
                  : undefined
              }
            />
          ) : null}

          {showPointsMenu ? (
            <PointsConfig
              disabled={!canConfigurePoints}
              page={page}
              readOnly={isPointsReadOnly}
              size="xs"
              title={getPointsConfigTooltip(
                sharedClassPage,
                canAuthorSection,
                assessments,
                pageOutcomes,
              )}
              isPageOverviewHeader
              onSave={(points) => savePage({ id: page.id, max_points: points })}
            />
          ) : null}

          {!isInTemplateSection && <DueDateButton page={page} readOnly={readOnly} />}

          {showLockedChip && <PageLockedChip canAuthorSection={canAuthorSection} />}
        </div>

        {isShowingStudentGroupCard && (
          <div className={className}>
            <StudentGroupCard
              buttonComponent={
                <LegacyButton
                  kind="primary"
                  size="xs"
                  onClick={() => setShowStudentGroupCard(false)}
                >
                  {t("common.close")}
                </LegacyButton>
              }
              pageGroup={pageGroups[pageGroupIndex]}
              pageGroupIndex={pageGroupIndex}
              isActive
              shouldFilterForReadiedUsers
            />
          </div>
        )}

        {isShowingGroupConfigurationCard && (
          <div className={className}>
            <GroupConfigCard page={page} pageGroups={pageGroups} />
          </div>
        )}

        {isShowingStudentSelfSelectCard && (
          <div className={className}>
            <StudentSelfSelectCard page={page} pageGroups={pageGroups} />
          </div>
        )}

        {!isProjectorView && <AssessmentBanner className={className} page={page} />}
      </div>
    </div>
  );
};
